import {
  FileDocumentDto,
  GetLogoBankResponse,
  IBankFileDocumentWithUrl,
} from "services/interface";
import styled from "@emotion/styled";
import { Col, Form, FormInstance, Row } from "antd";
import { FC, useEffect, useMemo, useRef, useState } from "react";
import BankDescriptionForm from "./BankDescriptionForm";
import { useGetMasterBankType } from "hooks/useMasterData";
import BankDocumentUpload, { DocumentSlot } from "./BankDocumentUpload";
import { getDocumentSlotKey, getSlotsForBankType } from "./bankDocumentConfig";

interface MainCreateBankProps {
  form: FormInstance;
  dataLogo: GetLogoBankResponse[] | undefined;
  onDocumentChange: (document_type: string, dto: FileDocumentDto | null) => void;
  initialDocuments?: IBankFileDocumentWithUrl[];
}

const MainCreateBank: FC<MainCreateBankProps> = ({
  form,
  dataLogo,
  onDocumentChange,
  initialDocuments,
}) => {
  const { data: dataBankType } = useGetMasterBankType();
  const watchedBankTypeId = Form.useWatch("bank_type_id", form);
  const prevBankTypeIdRef = useRef<typeof watchedBankTypeId>(undefined);
  const [bankTypeTouched, setBankTypeTouched] = useState(false);

  const selectedDescription = useMemo(() => {
    return (
      dataBankType?.data?.find((t) => String(t.id) === String(watchedBankTypeId))
        ?.description ?? ""
    );
  }, [dataBankType, watchedBankTypeId]);

  const documentSlots: DocumentSlot[] = useMemo(() => {
    const base = getSlotsForBankType(selectedDescription);
    const docsSource = bankTypeTouched ? undefined : initialDocuments;
    return base.map((slot) => {
      const init = docsSource?.find(
        (d) => getDocumentSlotKey(d) === slot.document_type
      );
      return {
        document_type: slot.document_type,
        label: slot.label,
        description: slot.label,
        required: slot.required,
        initialFileId: init?.file_id,
        initialFileUrl: init?.file_url,
      };
    });
  }, [selectedDescription, initialDocuments, bankTypeTouched]);

  useEffect(() => {
    if (
      prevBankTypeIdRef.current !== undefined &&
      prevBankTypeIdRef.current !== watchedBankTypeId
    ) {
      onDocumentChange("__reset__", null);
      setBankTypeTouched(true);
    }
    prevBankTypeIdRef.current = watchedBankTypeId;
  }, [watchedBankTypeId, onDocumentChange]);

  return (
    <FormWrapper>
      <FormItemWrapper>
        <Row gutter={[16, 16]} style={{ backgroundColor: "#EFEFEFEF" }}>
          <Col span={24}>
            <BankDescriptionForm
              dataLogo={dataLogo}
              dataBankType={dataBankType}
              is_active={true}
            />
          </Col>
          <Col span={24}>
            <BankDocumentUpload
              slots={documentSlots}
              onUploadComplete={(document_type, file_id, url) => {
                const slot = documentSlots.find(
                  (s) => s.document_type === document_type
                );
                onDocumentChange(document_type, {
                  file_id,
                  document_type,
                  name: document_type,
                  description: slot?.label ?? "",
                });
              }}
              onRemove={(document_type) => {
                onDocumentChange(document_type, null);
              }}
            />
          </Col>
        </Row>
      </FormItemWrapper>
    </FormWrapper>
  );
};

export default MainCreateBank;

const FormWrapper = styled.div`
  width: 100%;
  height: 100%;
  background-color: #efefefef;
`;

const FormItemWrapper = styled.div`
  width: 100%;
  height: 100%;
  padding: 10px 0px 10px 0px;
  border-radius: 10px;
`;
